import type { ParsedLocation } from '@tanstack/react-router'
import { redirect } from '@tanstack/react-router'

import { rememberPathAfterLogin } from '@/lib/login-return'
import { useAuthStore } from '@/stores/auth'
import { useContextStore } from '@/stores/context'

/**
 * Only attribution params ride along a guard redirect. Email links carry
 * one-shot credentials (?inviteId=, ?code=) that must not land on the dashboard
 * URL, where they stay in browser history and in the page_location attached to
 * every analytics event. An allowlist keeps a future email param safe by
 * default; a denylist would leak it until someone remembered to add it.
 */
const CARRIED_SEARCH_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'referrer',
  'ref',
  'via',
  'aff',
  'cta',
  'feature',
  'recurrence',
  'envelopes',
  'gclid',
  'fbclid',
  'device',
  'matchtype',
  'network',
  'adposition',
])

/**
 * Reads from the location the router hands the guard, not from window: inside
 * beforeLoad the history pushState is still queued in a microtask, so
 * window.location describes the page being left, not the one being entered.
 */
const getCarriedSearch = (
  location: GuardContext['location'],
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(location.search as Record<string, unknown>).filter(([key]) =>
      CARRIED_SEARCH_KEYS.has(key.toLowerCase()),
    ),
  )

interface GuardContext {
  location: Pick<ParsedLocation, 'pathname' | 'search'> & { href?: string }
}

/**
 * Basic authentication check - only verifies if user has valid token
 */
export const requireAuthOnly = ({ location }: GuardContext) => {
  const isAuthenticated = useAuthStore.getState().isAuthenticated()

  if (!isAuthenticated) {
    throw redirect({
      to: '/login',
      search: getCarriedSearch(location),
    })
  }
}

/**
 * Full authentication check - verifies token AND selected account
 * Redirects to /switch if no account is selected
 */
export const requireAuth = (context: GuardContext) => {
  // First check authentication
  requireAuthOnly(context)

  // Then check if user has selected an account
  const { selectedUserAccountId } = useContextStore.getState()

  if (!selectedUserAccountId) {
    throw redirect({
      to: '/switch',
      search: getCarriedSearch(context.location),
    })
  }
}

/**
 * Redirects authenticated users away from public auth pages (login, register, etc.)
 * Sends them to / if they have a selected account, or to /switch otherwise.
 */
export const redirectIfAuthenticated = ({ location }: GuardContext) => {
  const isAuthenticated = useAuthStore.getState().isAuthenticated()

  if (!isAuthenticated) return

  const { selectedUserAccountId } = useContextStore.getState()

  throw redirect({
    to: selectedUserAccountId ? '/' : '/switch',
    search: getCarriedSearch(location),
  })
}

export const requireAuthRemembering = (context: GuardContext) => {
  const { location } = context
  const isAuthenticated = useAuthStore.getState().isAuthenticated()
  const { selectedUserAccountId } = useContextStore.getState()

  if (isAuthenticated && selectedUserAccountId) return

  rememberPathAfterLogin(location.href ?? location.pathname)
  throw redirect({
    to: isAuthenticated ? '/switch' : '/login',
    search: getCarriedSearch(location),
  })
}
